import { post } from "~/common/request"
import {
  getUploadFileMimeType,
  getUploadedResourceKind,
  validateLocalImageDimensions,
  validateLocalUploadFile,
  VIDEO_UPLOAD_TARGET_SIZE_MEGABYTES,
  type UploadedResource,
} from "~/common/media/uploadedResources"
import { createMaterialFromResource, getResourceFromMaterial } from "./material"

type UploadFileOptions = {
  file: File | Blob
  filename?: string
  onProgress?: (progress: number) => void
  onVideoCompressionProgress?: (progress: number | null) => void
  signal?: AbortSignal
  type?: UploadResourceType
  /** Compression threshold and target in MB; defaults to 50. */
  videoTargetSizeMegabytes?: number
}

type UploadResourceType = API.Material.UploadResourceType

type UploadPayload = {
  name: string
  size: number
  type: string
  url: string
}

type UploadFileResult<T = unknown> = {
  data: T
  ok: boolean
  status: number
}

export class UploadFileValidationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "UploadFileValidationError"
  }
}

const generateString = (length: number) => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
  let result = ""

  for (let index = 0; index < length; index += 1) {
    result += characters.charAt(Math.floor(Math.random() * characters.length))
  }

  return result
}

const getUploadFilename = (file: File | Blob, filename?: string) => {
  if (filename) return filename
  if (file instanceof File) return file.name
  return "upload"
}

const getNewFilename = (filename: string) => {
  const randomName = generateString(15)
  const extensionIndex = filename.lastIndexOf(".")

  return extensionIndex <= 0
    ? randomName
    : `${randomName}.${filename.substring(extensionIndex + 1)}`
}

const getUploadResourceType = (
  file: File | Blob,
  type?: UploadResourceType,
): UploadResourceType => {
  if (type) return type

  const resourceKind = getUploadedResourceKind({
    name: file instanceof File ? file.name : "",
    type: file.type,
    url: "",
    uploadedAt: Date.now(),
  })

  switch (resourceKind) {
    case "video":
      return "video"
    case "audio":
      return "audio"
    default:
      return "image"
  }
}

const uploadToOss = async ({
  file,
  onProgress,
  signal,
  type,
}: {
  file: File
  onProgress?: (progress: number) => void
  signal?: AbortSignal
  type: UploadResourceType
}) => {
  onProgress?.(0)

  const { data: policy } = await post<API.Material.UploadPolicySign>(
    "/api/material/upload_policy",
    { upload_type: type } satisfies API.Material.UploadPolicyParams,
    { signal },
  )

  if (!policy) throw new Error("获取上传凭证失败")

  const filename = getNewFilename(file.name)

  await new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest()
    const form = new FormData()
    const abort = () => request.abort()
    const cleanup = () => signal?.removeEventListener("abort", abort)

    form.append("name", filename)
    form.append("policy", policy.policy)
    form.append("OSSAccessKeyId", policy.access_key)
    form.append("success_action_status", "200")
    form.append("signature", policy.sign)
    form.append("key", `${policy.folder}${filename}`)
    form.append("file", file)

    request.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) return

      onProgress?.(Math.round(5 + (event.loaded / event.total) * 90))
    })
    request.addEventListener("load", () => {
      cleanup()

      if (request.status >= 200 && request.status < 300) {
        resolve()
        return
      }

      reject(new Error(`上传失败：HTTP ${request.status}`))
    })
    request.addEventListener("error", () => {
      cleanup()
      reject(new Error("上传失败，请检查网络后重试"))
    })
    request.addEventListener("abort", () => {
      cleanup()
      reject(new DOMException("上传已取消", "AbortError"))
    })

    if (signal?.aborted) {
      reject(new DOMException("上传已取消", "AbortError"))
      return
    }

    signal?.addEventListener("abort", abort, { once: true })
    request.open("POST", policy.oss_host)
    request.send(form)
  })

  return `${policy.cdn_host}/${filename}`
}

export const uploadFile = async <T = UploadedResource>({
  file,
  filename,
  onProgress,
  onVideoCompressionProgress,
  signal,
  type,
  videoTargetSizeMegabytes = VIDEO_UPLOAD_TARGET_SIZE_MEGABYTES,
}: UploadFileOptions): Promise<UploadFileResult<T>> => {
  const uploadType = getUploadResourceType(file, type)
  const targetFile =
    file instanceof File
      ? file
      : new File([file], getUploadFilename(file, filename), {
          type: file.type || "application/octet-stream",
        })
  const validation = validateLocalUploadFile(targetFile, uploadType)

  if (!validation.valid) {
    throw new UploadFileValidationError(validation.message)
  }

  if (uploadType === "image") {
    const dimensionValidation = await validateLocalImageDimensions(targetFile)

    if (!dimensionValidation.valid) {
      throw new UploadFileValidationError(dimensionValidation.message)
    }
  }

  let preparedFile = targetFile

  if (
    uploadType === "video" &&
    targetFile.size > videoTargetSizeMegabytes * 1024 * 1024
  ) {
    const { compressVideoForUpload } =
      await import("~/common/media/videoCompression")
    preparedFile = await compressVideoForUpload(
      targetFile,
      onVideoCompressionProgress,
      videoTargetSizeMegabytes,
    )
  }

  const normalizedFile =
    preparedFile.type === getUploadFileMimeType(preparedFile, uploadType)
      ? preparedFile
      : new File([preparedFile], preparedFile.name, {
          type: getUploadFileMimeType(preparedFile, uploadType),
        })
  const url = await uploadToOss({
    file: normalizedFile,
    onProgress,
    signal,
    type: uploadType,
  })
  const data = {
    name: getUploadFilename(normalizedFile, filename),
    size: normalizedFile.size,
    type: normalizedFile.type || uploadType,
    url,
  } satisfies UploadPayload

  onProgress?.(95)

  const response = await createMaterialFromResource(data, signal)
  const resource = response.data
    ? getResourceFromMaterial(response.data)
    : { ...data, uploadedAt: Date.now() }

  onProgress?.(100)

  return {
    data: resource as T,
    ok: true,
    status: 200,
  }
}
